import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import communicationApi from '../../../../services/communicationApi';
import { Group, MessageComposition } from '../../../../types/communication';

const errorMessage = (e: unknown, fallback: string): string => (e instanceof Error && e.message ? e.message : fallback);

export const useGroupDetail = (groupId: string | null, open: boolean, onChanged: () => void) => {
    const [group, setGroup] = useState<Group | null>(null);
    const [models, setModels] = useState<MessageComposition[]>([]);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (!groupId) return;
        const [loaded, metas] = await Promise.all([
            communicationApi.getGroup(groupId),
            communicationApi.listMessageCompositions({ groupId, pageSize: 9999 }),
        ]);
        setGroup(loaded);
        setModels(metas.list);
    }, [groupId]);

    useEffect(() => {
        if (!open || !groupId) return;
        load().catch(() => toast.error('Grupo não encontrado.'));
    }, [open, groupId, load]);

    const run = async (action: () => Promise<unknown>, success: string, failure: string): Promise<boolean> => {
        setSaving(true);
        try {
            await action();
        } catch (e) {
            toast.error(errorMessage(e, failure));
            setSaving(false);
            return false;
        }
        toast.success(success);
        try {
            await load();
            onChanged();
        } catch {
            toast.warn('Ação concluída, mas não foi possível atualizar a tela. Recarregue para ver o estado atual.');
        } finally {
            setSaving(false);
        }
        return true;
    };

    return {
        group,
        models,
        saving,
        saveInfo: (name: string, description: string, defaultForNewContactPoints?: boolean) =>
            group
                ? run(
                      () =>
                          communicationApi.updateGroup(group.id, {
                              name,
                              description,
                              defaultForNewContactPoints,
                          }),
                      'Grupo atualizado.',
                      'Falha ao salvar.',
                  )
                : Promise.resolve(false),
        addUser: (login: string) =>
            group ? run(() => communicationApi.addUserToGroup(group.id, login), 'Pessoa adicionada ao grupo.', 'Falha ao adicionar.') : Promise.resolve(false),
        removeUser: (login: string, reason: string) =>
            group
                ? run(() => communicationApi.removeUserFromGroup(group.id, login, reason), 'Pessoa removida do grupo.', 'Falha ao remover.')
                : Promise.resolve(false),
        toggleStatus: (reason: string) =>
            group
                ? run(
                      () => communicationApi.toggleGroupStatus(group.id, reason),
                      group.status === 'ACTIVE' ? 'Grupo desativado.' : 'Grupo ativado.',
                      'Falha ao alterar status.',
                  )
                : Promise.resolve(false),
    };
};

export type GroupDetail = ReturnType<typeof useGroupDetail>;
